import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import { CRM_SEARCH_QUERY, useCrmSearch } from '../data/useCrmSearch';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import CustomerDataBoard from 'customer-data-board';
import { useMapDataForRender } from '../render/hooks/useMapDataForRender';
import { useHandleColumnCollapseChange } from '../stageSettings/hooks/useHandleColumnCollapse';
import { useHandlers } from '../handlers/useHandlers';
import { useGetRenderComponent } from '../render/renderComponents/useRenderComponents';
import { RegisterRenderComponents } from '../render/renderComponents/RegisterRenderComponents';
import { useReconciliation } from '../reconciliation/useReconciliation';
import { ReconciliationLocalMutationsProvider } from '../reconciliation/ReconciliationLocalMutationsProvider';
import { useAggregations } from '../aggregations/hooks/useAggregations';
import { useHandleCardClick } from '../card/hooks/useHandleCardClick';
import MessageBusProvider from 'crm-message-bus/MessageBusProvider';
import { FastContext, useFastDispatch } from '../context/FastContext';
import { useIsBulkActionsRedesignEnabled } from '../bulkActions/hooks/useIsBulkActionsRedesignEnabled';
import { PreloadProvider, PipelinePreloadProvider, usePipelinePreloadLoading } from 'crm-object-board-core/providers';
import { GET_PIPELINE_QUERY } from 'crm-object-board-core/pipeline';
import { CardPreloadProvider } from 'crm-object-board-card/providers';
import { ErrorState } from '../error/ErrorState';
import { useRenderOptions } from '../render/options/useRenderOptions';
import { useIsCardShaded } from '../card/hooks/useIsCardShaded';
import { useIsCardDraggable } from '../card/hooks/useIsCardDraggable';
import { BOARD_MOUNT_MARK } from '../performance/markers';
import MarkAndMeasureOnMount from '../performance/components/MarkAndMeasureOnMount';
import { BOARD_MOUNTED_MEASURE } from '../performance/measurements';
import { useMarkOnMount } from '../performance/hooks/useMarkOnMount';
import { TrackQueryProvider } from '../performance/components/TrackQueryProvider';
import { OverlayContainer } from '../overlay/OverlayContainer';
import { useHandleColumnChange } from '../columnChange/useHandleColumnChange';
import { useState, memo, useMemo, useCallback } from 'react';
import { CardProvider } from '../card/CardProvider';
import { AggregationProvider } from '../aggregations/components/AggregationProvider';
import { BulkActionsProvider } from '../bulkActions/components/BulkActionsProvider';
import { GenerateSearchQueryProvider } from '../data/generateSearchQuery/GenerateSearchQueryProvider';
import { useWorkflowIcon } from '../workflowIcon/useWorkflowIcon';
import { BoardErrorWrapper } from '../error/BoardErrorWrapper';
import { TrackErrorQueryProvider } from '../error/TrackErrorQueryProvider';
import { CardSettingsPanelProvider } from 'crm-object-board-card/providers/settings';
import { useBoardCardSectionsLoading } from 'crm-object-board-card/hooks';
import { useHasCardV2 } from 'crm-object-board-core/hooks';
import { CrmObjectBoardCardSettings, CrmObjectBoardCardWelcomeDialogWithUserSetting } from 'crm-object-board-card';
import { BoardCoreDataProvider } from 'crm-object-board-core/providers';
import { errorSlice } from '../error/errorStore';
import { wrapWithHandler } from '../utils/wrapWithHandler';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CrmObjectBoardInternal = /*#__PURE__*/memo(() => {
  const {
    data,
    loading,
    loadingMore
  } = useCrmSearch();
  const hasBoardCardV2 = useHasCardV2();
  const isBulkActionsRedesignEnabled = useIsBulkActionsRedesignEnabled();
  const reconciled = useReconciliation(data);
  useHandleColumnCollapseChange();
  useHandleCardClick();
  useHandleColumnChange();
  useIsCardShaded();
  useIsCardDraggable();
  useWorkflowIcon();
  useAggregations(data, reconciled);
  const dataArray = useMapDataForRender(reconciled, loading, loadingMore);
  const handlers = useHandlers();
  const renderCard = useGetRenderComponent('renderCard');
  const renderStyledCard = useGetRenderComponent('renderStyledCard');
  const renderHeaderSummary = useGetRenderComponent('renderHeader');
  const renderFooter = useGetRenderComponent('renderFooter');
  const renderEmpty = useGetRenderComponent('renderEmpty');
  const renderColumnLoadedMarker = useGetRenderComponent('renderColumnLoadedMarker');
  const renderBoardLoadedMarker = useGetRenderComponent('renderBoardLoadedMarker');
  const renderBoardQueryStartedMarker = useGetRenderComponent('renderBoardQueryStartedMarker');
  const renderBulkAction = useGetRenderComponent('renderBulkAction');
  const options = useRenderOptions();
  const [columnMounted, setColumnMounted] = useState(false);
  const hasResult = useMemo(() => (dataArray === null || dataArray === void 0 ? void 0 : dataArray.flatMap(column => column.cards).length) > 0, [dataArray]);
  const handleColumnMounted = useCallback(() => {
    setColumnMounted(true);
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [loading && /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderBoardQueryStartedMarker && renderBoardQueryStartedMarker(), /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      })]
    }), (!loading || hasResult) && dataArray && dataArray.length && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(CrmObjectBoardCardWelcomeDialogWithUserSetting, {}), /*#__PURE__*/_jsx(MarkAndMeasureOnMount, {
        measurement: BOARD_MOUNTED_MEASURE
      }), renderBoardLoadedMarker && renderBoardLoadedMarker(), renderColumnLoadedMarker && columnMounted && renderColumnLoadedMarker(), /*#__PURE__*/_jsx(CustomerDataBoard, {
        columns: dataArray,
        getIsCardShaded: options.getIsCardShaded,
        getDragPermissionsMessageForCard: options.getDragPermissionsMessageForCard,
        StyledCardComponent: renderStyledCard
        // TODO: We can remove this once card v2 is fully implemented
        ,
        CardComponent: renderCard,
        onColumnCollapse: handlers.onColumnCollapseChange,
        onShowMore: handlers.onLoadMore,
        onColumnChange: handlers.onColumnChange,
        onCardClick: handlers.onCardClick // casting needed since CDB types the input as unknown but we have a strong type
        ,
        renderHeaderSummary: renderHeaderSummary,
        renderFooter: renderFooter,
        renderEmpty: renderEmpty,
        onColumnMounted: handleColumnMounted,
        BulkActionsComponent: renderBulkAction,
        useReskin: hasBoardCardV2,
        isBulkActionsRedesignEnabled: isBulkActionsRedesignEnabled
      })]
    })]
  });
});
export const PreloadProviderWrapper = ({
  children
}) => {
  const pipelineLoading = usePipelinePreloadLoading();
  const cardSectionsLoading = useBoardCardSectionsLoading();
  return /*#__PURE__*/_jsx(PreloadProvider, {
    loading: pipelineLoading || cardSectionsLoading,
    appSettings: ['crm_board_settings', 'crmTagsSettings', 'crm_object_pipelines'],
    children: children
  });
};
export const BoardCoreDataWrapper = _ref => {
  let {
      children
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const dispatch = useFastDispatch();
  const onError = useCallback(error => {
    if (error) {
      dispatch(errorSlice.actions.setError(error));
    }
  }, [dispatch]);
  return /*#__PURE__*/_jsx(BoardCoreDataProvider, {
    objectTypeId: props.objectTypeId,
    pipelineId: props.pipelineId,
    logger: props.logger,
    onError: onError,
    appMetrics: props.appMetrics,
    forceCardV2: props.forceCardV2,
    children: children
  });
};
const CrmObjectBoardWrapper = props => {
  useMarkOnMount(BOARD_MOUNT_MARK);
  return /*#__PURE__*/_jsx(BoardErrorWrapper, {
    objectTypeId: props.objectTypeId,
    children: /*#__PURE__*/_jsx(FastContext, {
      children: /*#__PURE__*/_jsx(BoardCoreDataWrapper, Object.assign({}, props, {
        children: /*#__PURE__*/_jsx(TrackQueryProvider, {
          objectTypeId: props.objectTypeId,
          queries: [CRM_SEARCH_QUERY, GET_PIPELINE_QUERY],
          children: /*#__PURE__*/_jsx(TrackErrorQueryProvider, {
            children: /*#__PURE__*/_jsx(ErrorState, {
              children: /*#__PURE__*/_jsx(PipelinePreloadProvider, {
                pipelineId: props.pipelineId,
                children: /*#__PURE__*/_jsx(CardPreloadProvider, {
                  appId: props.appId,
                  children: /*#__PURE__*/_jsx(PreloadProviderWrapper, {
                    children: /*#__PURE__*/_jsx(GenerateSearchQueryProvider, {
                      searchQuery: props.searchQuery,
                      children: /*#__PURE__*/_jsxs(CardSettingsPanelProvider, {
                        children: [/*#__PURE__*/_jsx(CrmObjectBoardCardSettings, {}), /*#__PURE__*/_jsx(CardProvider, {
                          children: /*#__PURE__*/_jsx(BulkActionsProvider, {
                            hasBulkActions: props.hasBulkActions,
                            isBulkActionsRedesignEnabled: props.isBulkActionsRedesignEnabled,
                            children: /*#__PURE__*/_jsx(ReconciliationLocalMutationsProvider, {
                              newObjectIds: props.newObjectIds,
                              children: /*#__PURE__*/_jsx(AggregationProvider, {
                                children: /*#__PURE__*/_jsxs(MessageBusProvider, {
                                  children: [/*#__PURE__*/_jsx(OverlayContainer, {}), /*#__PURE__*/_jsx(RegisterRenderComponents, {
                                    renderEmpty: props.renderEmpty,
                                    renderColumnLoadedMarker: props.renderColumnLoadedMarker,
                                    renderBoardQueryStartedMarker: props.renderBoardQueryStartedMarker,
                                    renderBoardLoadedMarker: props.renderBoardLoadedMarker,
                                    renderCardLoadedMarker: wrapWithHandler(props.renderCardLoadedMarker),
                                    children: /*#__PURE__*/_jsx(CrmObjectBoardInternal, {})
                                  })]
                                })
                              })
                            })
                          })
                        })]
                      })
                    })
                  })
                })
              })
            })
          })
        })
      }))
    })
  });
};

// This will be removed once the old pipeline board is fully deprecated.
export const CrmObjectBoard = /*#__PURE__*/memo(CrmObjectBoardWrapper);

// New export for backwards compatibility with the old pipeline board.
export const CrmObjectPipelineBoard = CrmObjectBoard;